import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function calculateFees(price: number) {
  const fee_percentage = 20;
  const platformFee = price * (fee_percentage / 100);
  const sellerAmount = price - platformFee;

  return {
    total: price,
    platformFee,
    sellerAmount,
  };
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(price);
}

export function formatCurrency(amount: number, maxDecimals: number = 2): string {
  // For very small amounts, show more decimal places to avoid showing £0.00
  const needsMoreDecimals = amount > 0 && amount < 0.01;
  const actualMaxDecimals = needsMoreDecimals ? 8 : 2;

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: actualMaxDecimals,
  }).format(amount);
}

export function formatLastUpdated(value: string): string {
  if (!value) return 'Unknown';

  // Handle special cases
  if (value === 'all_time') {
    return 'All time';
  }

  if (value === '0_days') {
    return 'Today';
  }

  // Handle time periods in format "1_day", "2_days", "1_month", etc.
  const timePattern = /^(\d+)_(day|days|week|weeks|month|months|year|years)$/;
  const match = value.match(timePattern);

  if (match) {
    // Format as "X days ago", "X months ago", etc.
    const [_, number, unit] = match;
    return `${number} ${unit} ago`;
  }

  // Handle legacy formats for backward compatibility
  switch (value) {
    // Legacy "within_last_*" format
    case 'within_last_day':
      return 'Within the last day';
    case 'within_last_3_days':
      return 'Within the last 3 days';
    case 'within_last_week':
      return 'Within the last week';
    case 'within_last_14_days':
      return 'Within the last 14 days';
    case 'within_last_month':
      return 'Within the last month';
    case 'within_last_90_days':
      return 'Within the last 90 days';
    case 'within_last_180_days':
      return 'Within the last 180 days';
    case 'within_last_year':
      return 'Within the last year';

    // Legacy specific time periods
    case '1_month':
      return '1 month ago';
    case '3_months':
      return '3 months ago';
    case '6_months':
      return '6 months ago';
    case '1_year':
      return '1 year ago';
    default:
      // Replace underscores with spaces and capitalize first letter
      const formatted = value.replace(/_/g, ' ');
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
}

export interface DateFormatOptions {
  locale?: string;
  format?: 'full' | 'long' | 'medium' | 'short' | 'custom';
  customOptions?: Intl.DateTimeFormatOptions;
}

/**
 * Unified date formatter that handles various date formats consistently
 * @param date A Date object, timestamp (number), or date string
 * @param options Configuration options for formatting
 * @returns Formatted date string
 */
export function formatDate(date: Date | number | string, options: DateFormatOptions = {}): string {
  // Default options
  const { locale = 'en-GB', format = 'medium', customOptions } = options;

  // Convert input to Date object
  const dateObj =
    date instanceof Date ? date : typeof date === 'number' ? new Date(date) : new Date(date);

  // Define format presets
  const formatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    full: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    short: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
  };

  // Use custom options if provided, otherwise use preset
  const dateTimeOptions = customOptions || formatOptions[format];

  try {
    return new Intl.DateTimeFormat(locale, dateTimeOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format relative time (e.g., "2 days ago", "in 3 months")
 * @param date A Date object, timestamp (number), or date string
 * @param options Configuration options
 * @returns Formatted relative time string
 */
export function formatRelativeTime(
  date: Date | number | string,
  options: { locale?: string } = {},
): string {
  const { locale = 'en-GB' } = options;

  // Convert input to Date object
  const dateObj =
    date instanceof Date ? date : typeof date === 'number' ? new Date(date) : new Date(date);

  const now = new Date();
  const diffInMs = dateObj.getTime() - now.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffInDays) > 30) {
    return formatDate(dateObj, { locale, format: 'medium' });
  } else if (Math.abs(diffInDays) > 0) {
    return rtf.format(diffInDays, 'day');
  } else if (Math.abs(diffInHours) > 0) {
    return rtf.format(diffInHours, 'hour');
  } else if (Math.abs(diffInMinutes) > 0) {
    return rtf.format(diffInMinutes, 'minute');
  } else {
    return rtf.format(diffInSeconds, 'second');
  }
}
